// Correct-by-construction replacement for legacy/dashboard.html's
// `groupDataIntoPeriodsAligned` -- see ops/dashboard-client-audit.md
// finding 8 for the two root causes this fixes:
//
//   Bug A: legacy anchored the newest bucket's end to the last *whole
//   hour* (`alignedHour.setMinutes(0,0,0)`), not to "now" -- so up to 59
//   minutes of the freshest data never appeared on the chart at all, and
//   the "current" period was always already-completed, never partial.
//
//   Bug B: legacy filled empty buckets with a fabricated high=avg=low=1.0,
//   rendered identically to a real flat reading -- a genuine data gap was
//   indistinguishable from an artifact of the code.
//
// Fix, by construction:
//   - Every bucket's [start, end) boundary is derived from a single `now`
//     timestamp captured once per render pass (`computeWindow` below) --
//     the newest bucket always ends exactly at `now`, is genuinely partial
//     (still accumulating), and is the same `now` the fetch range is
//     derived from, so there is exactly one source of truth for "when is
//     this window," not two independently-computed values that merely
//     happen to agree.
//   - A bucket with zero contributing samples is `hasData: false` with
//     `high/low/avg: null` -- never a numeric stand-in. Rendering code
//     must treat `hasData: false` as an explicit gap, not skip the check.

export interface RatioPoint {
  tsMs: number;
  ratio: number;
  /** T-156: true per-second ratio extremes across every book state in that
   * second (API's ratio_window_max/min). Null/absent on rows predating the
   * column -- the sampled `ratio` stands in. Only high/low use these; the
   * avg and bullish/bearish means stay on the sampled ratio so extremes
   * never inflate a mean. */
  windowMax?: number | null;
  windowMin?: number | null;
}

/** A rollup bar treated as a time span with its own pre-aggregated
 * high/low/avg (btclock-ingest's ratio_rollup_{1m,15m,1h} tables) --
 * spans and points are aggregated into chart buckets through one shared
 * path (see `bucketize`) rather than two parallel implementations. */
export interface RatioSpan {
  startMs: number;
  endMs: number;
  high: number;
  low: number;
  avg: number;
  weight: number; // tick_count -- for weighting the bucket's average
  /** The span's own real bullish/bearish split (2026-07-22), computed by
   * btclock-ingest's rollup.py from the real per-tick data at rollup time
   * -- null if the span predates this column, or genuinely has no tick on
   * that side. Weighted into the bucket by `weight`, same as `avg`. */
  bullishAvg: number | null;
  bearishAvg: number | null;
}

export interface Bucket {
  startMs: number;
  endMs: number;
  hasData: boolean;
  high: number | null;
  low: number | null;
  avg: number | null;
  /** Mean of ticks strictly above/below 1.0 in this bucket (legacy's
   * `bullishAvg`/`bearishAvg`), matching legacy exactly -- `null` if no
   * tick satisfies that side. Populated from raw `RatioPoint` ticks when
   * available, or from `RatioSpan.bullishAvg`/`bearishAvg` (computed by
   * btclock-ingest at rollup time from the real per-tick data, not
   * re-derived from the span's already-blended `avg`) when the window is
   * wide enough that only rollup bars are available -- both paths are the
   * real per-tick mean, weighted the same way `avg` is when multiple spans
   * land in one bucket. `null` for spans that predate this column existing
   * (older rollup rows never had a split computed for them). */
  bullishAvg: number | null;
  bearishAvg: number | null;
  /** True only for the single newest bucket, which ends at "now" and is
   * therefore still accumulating -- SPEC.md's "current partial period
   * rendered" requirement. Every other bucket has already fully elapsed. */
  isPartial: boolean;
}

export interface Window {
  sinceMs: number;
  untilMs: number;
}

/** One shared derivation for both "what range to fetch" and "where do
 * bucket boundaries fall" -- the exact thing Finding 8 calls out as
 * missing from the legacy code (fetch range and bucket boundaries were two
 * separately-computed values that happened to agree, not one shared
 * value). `marginMs` pads the requested fetch range beyond the nominal
 * bucket span to absorb the small, unavoidable clock/latency gap between
 * "when the client captured `now`" and "when the server computes its own
 * `now` for the query" -- without it, the oldest bucket could be very
 * slightly under-filled at the edge. */
export function computeWindow(
  periodMs: number,
  numPeriods: number,
  nowMs: number,
  marginMs: number = Math.max(periodMs, 5 * 60_000)
): Window {
  return {
    sinceMs: nowMs - periodMs * numPeriods - marginMs,
    untilMs: nowMs,
  };
}

export function bucketize(
  points: RatioPoint[],
  spans: RatioSpan[],
  periodMs: number,
  numPeriods: number,
  nowMs: number
): Bucket[] {
  const buckets: Bucket[] = [];
  for (let k = 0; k < numPeriods; k++) {
    const endMs = nowMs - (numPeriods - 1 - k) * periodMs;
    const startMs = endMs - periodMs;
    buckets.push({
      startMs,
      endMs,
      hasData: false,
      high: null,
      low: null,
      avg: null,
      bullishAvg: null,
      bearishAvg: null,
      isPartial: k === numPeriods - 1,
    });
  }

  // Points are assigned to the bucket whose [start, end) contains their
  // timestamp -- exact, no simplification needed since a point has no
  // duration of its own.
  for (const p of points) {
    const bucket = findBucket(buckets, p.tsMs);
    if (!bucket) continue;
    accumulatePoint(bucket, p);
  }

  // Spans (rollup bars) are assigned by their *start* time falling inside
  // a bucket. This is a deliberate, documented simplification: a span
  // whose start is just before a bucket boundary but which extends across
  // it is attributed entirely to the earlier bucket rather than split
  // proportionally across both. Given rollup bars are at most 1h wide and
  // chart buckets are at minimum 5m wide, this can misattribute up to one
  // rollup period's worth of data at a bucket edge -- an accepted,
  // bounded approximation, never a fabrication (every value shown still
  // comes from a real span that really has that high/low/avg).
  for (const s of spans) {
    const bucket = findBucket(buckets, s.startMs);
    if (!bucket) continue;
    accumulateSpan(bucket, s);
  }

  return buckets;
}

function findBucket(buckets: Bucket[], tsMs: number): Bucket | undefined {
  return buckets.find((b) => tsMs >= b.startMs && tsMs < b.endMs);
}

interface Accumulator {
  highSum: number;
  lowSum: number;
  weightedAvgSum: number;
  weightSum: number;
  // Weighted the same way as weightedAvgSum/weightSum, but only over
  // spans/points that actually had a value on that side -- a span/point
  // with a null bullishAvg (nothing bullish that period) contributes zero
  // weight to bullishWeightSum, not a zero value at full weight, so it
  // can never drag a real average toward zero.
  bullishWeightedSum: number;
  bullishWeightSum: number;
  bearishWeightedSum: number;
  bearishWeightSum: number;
}

const accumulators = new WeakMap<Bucket, Accumulator>();

function getAccumulator(bucket: Bucket): Accumulator {
  let acc = accumulators.get(bucket);
  if (!acc) {
    acc = {
      highSum: -Infinity,
      lowSum: Infinity,
      weightedAvgSum: 0,
      weightSum: 0,
      bullishWeightedSum: 0,
      bullishWeightSum: 0,
      bearishWeightedSum: 0,
      bearishWeightSum: 0,
    };
    accumulators.set(bucket, acc);
  }
  return acc;
}

function accumulatePoint(bucket: Bucket, p: RatioPoint): void {
  // A raw tick's own bullish/bearish contribution is itself, strictly
  // >/< 1.0, matching legacy's per-tick filter exactly -- reuses the same
  // weighted-span accumulation path a rollup bar's already-computed split
  // goes through, rather than a second parallel implementation. T-156: the
  // tick's high/low contribution is the true per-second extreme when
  // present, coalesced to the sampled ratio when null/absent; avg and the
  // bullish/bearish split stay the sampled ratio (means must not be
  // inflated by extremes).
  accumulateSpan(bucket, {
    high: p.windowMax ?? p.ratio,
    low: p.windowMin ?? p.ratio,
    avg: p.ratio,
    weight: 1,
    bullishAvg: p.ratio > 1.0 ? p.ratio : null,
    bearishAvg: p.ratio < 1.0 ? p.ratio : null,
  });
}

function accumulateSpan(bucket: Bucket, s: RatioSpan | Omit<RatioSpan, "startMs" | "endMs">): void {
  const acc = getAccumulator(bucket);
  acc.highSum = Math.max(acc.highSum, s.high);
  acc.lowSum = Math.min(acc.lowSum, s.low);
  acc.weightedAvgSum += s.avg * s.weight;
  acc.weightSum += s.weight;
  if (s.bullishAvg !== null) {
    acc.bullishWeightedSum += s.bullishAvg * s.weight;
    acc.bullishWeightSum += s.weight;
  }
  if (s.bearishAvg !== null) {
    acc.bearishWeightedSum += s.bearishAvg * s.weight;
    acc.bearishWeightSum += s.weight;
  }

  bucket.hasData = true;
  bucket.high = acc.highSum;
  bucket.low = acc.lowSum;
  bucket.avg = acc.weightSum > 0 ? acc.weightedAvgSum / acc.weightSum : null;
  bucket.bullishAvg = acc.bullishWeightSum > 0 ? acc.bullishWeightedSum / acc.bullishWeightSum : null;
  bucket.bearishAvg = acc.bearishWeightSum > 0 ? acc.bearishWeightedSum / acc.bearishWeightSum : null;
}
